import { createRegistrationAttribution } from '~/utils/registration-attribution.mjs'
import { getGrowthEventAnonymousId } from '~/utils/growthEvents'
import { analyticsLanding } from '~/utils/analytics-acquisition.mjs'
import { contentContext } from '~/utils/content-catalog.mjs'
import { createContentAttribution, entrySource } from '~/utils/content-attribution.mjs'
import { createAnalyticsDispatcher, analyticsPath } from '~/utils/analytics-core.mjs'
import { createClarityConsent } from '~/utils/clarity-consent.mjs'
import { detectAnalyticsCountry, resolveAnalyticsConsent } from '~/utils/analytics-region.mjs'

export default defineNuxtPlugin(app => {
  const config = useRuntimeConfig().public
  const enabled = config.analyticsEnabled === true && config.analyticsPropertyReady === true && /^G-[A-Z0-9]+$/.test(String(config.analyticsMeasurementId || ''))
  const analyticsReady = useState('analytics-ready', () => false)
  const available = useState('analytics-available', () => false)
  const choice = useState<'unknown' | 'granted' | 'denied'>('analytics-choice', () => 'unknown')
  const regionReady = useState('analytics-region-ready', () => false)
  available.value = enabled
  if (!enabled) return
  const clarity = createClarityConsent(window, document)
  const id = String(config.analyticsMeasurementId), key = 'musicmint:analytics-consent:v1'
  const win = window as any
  let loading = false, loaded = false, configured = false
  let explicitChoice = 'unknown', country: string | null = null
  const privacySignal = (window.navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  function raw(..._args: any[]) { (win.dataLayer ||= []).push(arguments) }
  const registration = createRegistrationAttribution({ storage: () => localStorage, location: window.location, referrer: document.referrer, document, anonymousId: getGrowthEventAnonymousId })
  registration.capture(false)
  let accountID: string | null = null
  function syncIdentity() {
    if (choice.value !== 'granted') return
    const fields = registration.params()
    raw('set', { user_id: accountID, anonymous_id: fields.anonymous_id })
    if (accountID) window.clarity?.('identify', accountID)
    else window.clarity?.('identify', fields.anonymous_id)
    if (fields.anonymous_id) window.clarity?.('set', 'anonymous_id', fields.anonymous_id)
    if (fields.ga_client_id) window.clarity?.('set', 'ga_client_id', fields.ga_client_id)
  }
  const attribution = createContentAttribution({ storage: () => sessionStorage })
  let firstPage = true, lastContentPage = ''
  const page = () => analyticsPath(app.$router.currentRoute.value.path)
  let landing: ReturnType<typeof analyticsLanding> | undefined
  let currentContext: Record<string, string> = {}, previousPage = '', previousPath = ''
  const dispatcher = createAnalyticsDispatcher({send(name: string, params: Record<string, unknown>, context: Record<string, string> = {}) {
    raw('event', name, {...params, page_location: window.location.origin + (params.page_path || page()), page_title: 'MusicMint', ...context})
  }})
  function trackPage(path: string) {
    if (choice.value !== 'granted') return
    const clean = window.location.origin + analyticsPath(path)
    // The actual route identifies navigation only in memory; outgoing URLs stay templated.
    if (path !== previousPath) {
      currentContext = firstPage && landing ? { ...landing } : { page_location: clean, page_referrer: previousPage }
      previousPage = clean
      previousPath = path
    }
    const source = firstPage ? entrySource(document.referrer, window.location.origin) : 'internal'
    firstPage = false
    const context = attribution.visit(path, source, document.documentElement.lang || '')
    dispatcher.page(path, { ...currentContext })
    if (contentContext(path).content_id && lastContentPage !== path) dispatcher.track('content_view', {...context, page_path:path}, undefined, { ...currentContext })
    lastContentPage = path
  }
  function track(name: string, params: Record<string, unknown> = {}) {
    if (choice.value !== 'granted') return false
    if (['seo_landing_create_click', 'geo_guide_cta_click', 'rap_name_create_click', 'rap_lyrics_create_click', 'lyrics_create_click'].includes(name)) {
      const path = String(params.page_path || app.$router.currentRoute.value.path)
      const context = attribution.visit(path, 'internal', document.documentElement.lang || '')
      dispatcher.track('content_cta_click', {...context, ...params, page_path:path}, undefined, { ...currentContext })
    }
    return dispatcher.track(name, {page_path:page(), ...params}, undefined, { ...currentContext })
  }
  // Legacy components cannot bypass the same consent and parameter filtering gates.
  window.gtag = (command: string, name: string, params: Record<string, unknown>) => { if (command === 'event') track(name, params) }
  function activate() {
    if (!loaded || choice.value !== 'granted') return
    win[`ga-disable-${id}`] = false
    raw('consent', 'update', {analytics_storage:'granted', ad_storage:'denied', ad_user_data:'denied', ad_personalization:'denied'})
    if (!configured) {
      raw('js', new Date())
      raw('config', id, {send_page_view:false, allow_google_signals:false, allow_ad_personalization_signals:false, ...landing, page_title:'MusicMint'})
      configured = true
    }
    syncIdentity()
    raw('get', id, 'client_id', (clientID: string) => {
      if (choice.value !== 'granted') return
      registration.setGA(clientID); syncIdentity()
    })
    dispatcher.setReady(true)
    analyticsReady.value = true
  }
  function load() {
    if (loaded) { activate(); return }
    if (loading) return
    loading = true
    const script = document.createElement('script')
    script.async = true
    script.referrerPolicy = 'no-referrer'
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
    script.onload = () => { loaded = true; loading = false; activate() }
    script.onerror = () => { loading = false; script.remove() }
    document.head.appendChild(script)
  }
  function apply(value: string, awaitingInitialRegion = false) {
    const next = value === 'granted' ? 'granted' : value === 'denied' ? 'denied' : 'unknown'
    choice.value = next
    dispatcher.setConsent(next)
    clarity.setConsent(next)
    if (next === 'granted') {
      registration.capture(); syncIdentity()
      if (!landing) landing = analyticsLanding(window.location.href || window.location.origin + app.$router.currentRoute.value.path, document.referrer, window.location.origin)
      trackPage(app.$router.currentRoute.value.path)
      load()
    } else {
      // Keep previously permitted data unread while this reload resolves its
      // region. An unknown result, rejection or privacy reset still clears it.
      if (!awaitingInitialRegion) {
        attribution.clear()
        registration.clearIDs()
        try { sessionStorage.removeItem('musicmint:analytics-success:v1') } catch { /* no persistence */ }
      }
      analyticsReady.value = false
      landing = undefined; currentContext = {}; previousPage = ''; previousPath = ''
      firstPage = true
      lastContentPage = ''
      win[`ga-disable-${id}`] = true
      dispatcher.setReady(false)
      if (loaded) raw('consent', 'update', {analytics_storage:'denied', ad_storage:'denied', ad_user_data:'denied', ad_personalization:'denied'})
    }
  }
  function updateChoice(value: string, persist = true, awaitingInitialRegion = false) {
    explicitChoice = value === 'granted' ? 'granted' : value === 'denied' ? 'denied' : 'unknown'
    if (persist) { try { localStorage.setItem(key, explicitChoice) } catch { /* This tab still honors the choice. */ } }
    apply(resolveAnalyticsConsent({ choice: explicitChoice, country, privacySignal }), awaitingInitialRegion && explicitChoice === 'unknown' && !privacySignal)
  }
  win[`ga-disable-${id}`] = true
  raw('consent', 'default', {analytics_storage:'denied', ad_storage:'denied', ad_user_data:'denied', ad_personalization:'denied'})
  window.musicmintAnalytics = {identify:(value: string | null)=>{ accountID = value; syncIdentity() }, acquisition:()=>registration.params(choice.value === 'granted'), track, setConsent:updateChoice, granted:()=>choice.value === 'granted', content:()=>choice.value === 'granted' ? attribution.current() : {}}
  let saved = 'unknown'
  try { saved = localStorage.getItem(key) || 'unknown' } catch { /* Default deny. */ }
  updateChoice(saved, false, true)
  // A stored explicit choice wins everywhere. Automatic regional permission is
  // never saved as consent, so visiting the EEA later still asks for a choice.
  let regionPending = false, retryRegionAfter = 0
  async function refreshRegion() {
    if (country || explicitChoice !== 'unknown' || privacySignal || regionPending || Date.now() < retryRegionAfter) return
    regionPending = true
    try {
      country = await detectAnalyticsCountry()
      regionReady.value = true
      // A choice made while the request was pending still takes precedence.
      apply(resolveAnalyticsConsent({ choice: explicitChoice, country, privacySignal }))
    } finally {
      regionPending = false
      retryRegionAfter = Date.now() + 30000
    }
  }
  if (explicitChoice !== 'unknown' || privacySignal) regionReady.value = true
  else void refreshRegion()
  window.addEventListener('online', () => { retryRegionAfter = 0; void refreshRegion() })
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void refreshRegion() })
  app.$router.afterEach((to, _from, failure) => { if (!failure) trackPage(to.path) })
  window.addEventListener('storage', event => {
    if (event.key === key || event.key === null) updateChoice(event.newValue || 'unknown', false)
  })
})
